/**
 * App shell — boot sequence + React Router 6 data-router wiring.
 *
 * Boot phases (Requirements 1.5, 4.2, 6.7, 8.7, 9.6, 12.2, 16.1):
 *
 *   1. Open the Conversation_Store (Dexie, with in-memory fallback);
 *      capture the `inMemoryFallback` flag so we can surface the
 *      persistence-unavailable banner (Requirement 12.2).
 *   2. Read the persisted API key (override) from the store. If
 *      present, install it via `setApiKeyOverride` so subsequent
 *      `getApiKey()` calls resolve via the runtime override
 *      (Requirement 6.7). The build-time `VITE_GEMINI_API_KEY` is
 *      consulted by `getApiKey()` as a second priority — both are
 *      free to coexist, the override wins.
 *   3. Hydrate the Zustand slices in dependency order so each slice
 *      sees the data it needs from earlier slices: preferences →
 *      profile → companion name → theme → naming flow → threads.
 *   4. Construct the Model_Router executor and attach it to the
 *      diagnostics slice. The executor is also stashed on
 *      `globalThis` so the Composer→executor wiring (Task 23.1) can
 *      reach it without a prop drill that this milestone does not
 *      need yet.
 *   5. Decide whether to render the Setup screen (no key configured)
 *      or the chat surface (key present). The decision is reflected
 *      both in the boot state union and in the route table — `/`
 *      redirects to `/setup` when no key is configured.
 *   6. Register the service worker via `vite-plugin-pwa`. The
 *      `registerType: 'prompt'` mode hands us an `onNeedRefresh` hook
 *      which we surface as a Toast so the End_User opts into the new
 *      version explicitly (Task 22.2, Requirement 9.6). Failures are
 *      swallowed — a broken SW must never crash boot.
 *
 * Global keyboard / a11y wiring (Tasks 21.1–21.2):
 *
 *   - `aria-live` polite region (`#aria-live-status`) is mounted at
 *     the App root so any component can write status updates to a
 *     single, screen-reader-announced surface (Requirement 10.3).
 *   - Global shortcut `?` and `Ctrl/Cmd + ,` open Settings; `Esc` is
 *     handled per-component by Modal/Toast already.
 *   - "Skip to main content" link is the first focusable element so
 *     keyboard users can bypass the sidebar (Requirement 10.2).
 *
 * The router is built only after boot resolves so the routes can
 * subscribe to fully-hydrated stores without the "render before
 * hydrate" flash.
 *
 * Validates: Requirements 1.5, 4.2, 6.7, 8.7, 9.6, 10.2, 10.3, 12.2,
 * 13.1, 13.3, 16.1.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { Toast } from './components/common';
import { I18nProvider } from './components/I18nProvider';
import { ThemeStage } from './components/theme';
import type { ModelRouterExecutor } from './domain/modelRouter/executor';
import { createModelRouterExecutor } from './domain/modelRouter/executor';
import type { LanguageCode } from './domain/types';
import type { ChatPipeline } from './infra/chatPipeline';
import { createChatPipeline } from './infra/chatPipeline';
import { getApiKey, setApiKeyOverride } from './infra/geminiClient';
import { openConversationStore } from './infra/store';
import {
  installAutoSync,
  pullAndApply,
  isSyncEnabled,
  resumeAutoSync,
  suspendAutoSync,
} from './infra/firebaseSync';
import {
  setActiveConversationStore,
  getActiveConversationStore,
  useCompanionNameStore,
  useDiagnosticsStore,
  useNamingFlowStore,
  usePreferencesStore,
  useProfileStore,
  useThemeStore,
  useThreadsStore,
} from './state';
import { ChatRoute } from './routes/ChatRoute';
import { MusicRoute } from './routes/MusicRoute';
import { SetupRoute } from './routes/SetupRoute';
import { LockRoute } from './routes/LockRoute';
import { isDeviceTrusted } from './infra/lock';
import { isGameFeatureEnabled } from './game/featureFlag';

import './global.css';

const GameRoute = lazy(async () => {
  const module = await import('./game/GameRoute');
  return { default: module.GameRoute };
});

/**
 * Boot state union — drives both the splash/setup/ready render
 * branching and the redirect-to-`/setup` rule on the `/` route.
 */
type BootState =
  | { kind: 'loading' }
  | { kind: 'setup' }
  | { kind: 'ready'; initialLanguage: LanguageCode }
  | { kind: 'error'; message: string };

/**
 * Shape of the global stash used to expose the executor and the
 * end-to-end chat pipeline to the route layer (Task 23.1) without
 * adding a context provider this milestone does not need.
 */
interface ExecutorGlobal {
  __executor?: ModelRouterExecutor;
  __pipeline?: ChatPipeline;
}

/**
 * Register the PWA service worker and wire the "needs refresh" prompt.
 *
 * The function is `async` so we can `await import()` the
 * `virtual:pwa-register` module — that module only exists when the
 * PWA plugin runs (so dev mode + tests get a graceful no-op).
 *
 * Returns a tuple of the `updateSW(reload?)` callback (so the caller
 * can trigger the SW skip-waiting flow once the End_User confirms
 * the prompt) and an `onNeedRefresh` flag so the App can render the
 * update Toast.
 */
async function registerServiceWorker(
  onNeedRefresh: () => void,
  onOfflineReady: () => void,
): Promise<((reload?: boolean) => Promise<void>) | null> {
  try {
    // The dynamic import keeps the SW glue out of the initial bundle
    // and lets non-PWA dev/test runs short-circuit cleanly.
    const mod = await import('virtual:pwa-register');
    const updateSW = mod.registerSW({
      immediate: false,
      onNeedRefresh,
      onOfflineReady,
      onRegisterError(err: unknown) {
        // eslint-disable-next-line no-console
        console.warn('[pwa] SW registration failed:', err);
      },
    });
    return updateSW;
  } catch (err) {
    // `virtual:pwa-register` is unavailable in dev mode and unit
    // tests — that is fine, the app simply runs without offline
    // support in those contexts.
    // eslint-disable-next-line no-console
    console.info('[pwa] Service worker registration skipped:', err);
    return null;
  }
}

export default function App(): ReactElement {
  const [boot, setBoot] = useState<BootState>({ kind: 'loading' });
  const [persistenceWarning, setPersistenceWarning] = useState<boolean>(false);
  // The lock gate is independent of the boot state — even after boot
  // resolves to `ready`, the App keeps rendering `<LockRoute />` until
  // the device is trusted. Devices become trusted after the first
  // successful PIN setup or verification, so a returning End_User on
  // the same browser/device never sees the lock screen again. Opening
  // the app on a brand-new device prompts for the PIN once before
  // that device too becomes trusted.
  const [unlocked, setUnlocked] = useState<boolean>(() => isDeviceTrusted());
  const [updatePrompt, setUpdatePrompt] = useState<{
    open: boolean;
    apply: () => void;
  }>({ open: false, apply: () => {} });

  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        // eslint-disable-next-line no-console
        console.info('[sync] boot starting, syncEnabled=', isSyncEnabled());
        // 1. Open store (Dexie or in-memory fallback).
        const { store: rawStore, inMemoryFallback } = await openConversationStore();
        if (cancelled) return;
        // Wrap the store so every successful write is mirrored to
        // the sync server (no-op when VITE_SYNC_URL is unset).
        const store = installAutoSync(rawStore);
        setActiveConversationStore(store, inMemoryFallback);
        if (inMemoryFallback) setPersistenceWarning(true);

        // 2. Read persisted API key, install as override if present.
        //    Migration: the original embedded Gemini key was revoked.
        //    A device that stored that now-dead key as its override
        //    would keep failing every request (the override always
        //    wins over the build-time key), so we drop the revoked key
        //    here and fall back to the working embedded key. Chat
        //    history, profile, and preferences are left untouched.
        const REVOKED_API_KEYS: readonly string[] = [
          'AIzaSyB2qGboUe2H7OJfM-fVa0jtpcmURsUbrhk',
        ];
        const persistedKey = await store.getApiKey();
        if (persistedKey !== null && REVOKED_API_KEYS.includes(persistedKey)) {
          // Clear only the dead key; everything else stays as-is.
          await store.setApiKey(null);
        } else if (persistedKey !== null) {
          setApiKeyOverride(persistedKey);
        }

        // 2b. If the device is already trusted (PIN configured AND
        // marked trusted on a previous launch), the user-key for
        // sync is derivable right now — pull the saved bundle from
        // the server before hydrating the slices so they hydrate
        // from the up-to-date cross-device state instead of whatever
        // this device had cached locally. When sync is unconfigured
        // or the network is down, this is a silent no-op.
        if (isDeviceTrusted() && isSyncEnabled()) {
          try {
            await pullAndApply(store);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('[sync] boot pull failed:', err);
          }
        }

        // 3. Hydrate slices in dependency order. Auto-sync is still
        //    SUSPENDED during this step (see installAutoSync's docs)
        //    so the default-thread creation that runs when this is
        //    a brand-new device cannot push back over the freshly
        //    pulled remote bundle.
        await usePreferencesStore.getState().hydrate();
        await useProfileStore.getState().hydrate();
        await useCompanionNameStore.getState().hydrate();
        await useThemeStore.getState().hydrate();
        useNamingFlowStore
          .getState()
          .hydrate(useCompanionNameStore.getState().name);
        await useThreadsStore.getState().hydrate();

        // 3b. Hydration is complete — let auto-sync mirror writes.
        //     If we never pulled (no PIN yet, sync disabled, network
        //     down), this is the moment when the local default
        //     state becomes the source of truth and the next user
        //     edit will push the first version of the bundle.
        resumeAutoSync();

        // 4. Create Model_Router executor and attach to diagnostics.
        //    Build the chat pipeline on top of it and stash both on
        //    `globalThis` so the route layer (`ChatRoute.handleSend`)
        //    can call `pipeline.send(text)` without prop-drilling
        //    through the router (Task 23.1).
        const executor = createModelRouterExecutor();
        useDiagnosticsStore.getState().attach(executor);
        const pipeline = createChatPipeline({ executor });
        const stash = globalThis as ExecutorGlobal;
        stash.__executor = executor;
        stash.__pipeline = pipeline;

        if (cancelled) return;

        // 5. Decide setup vs. ready.
        if (getApiKey() === null) {
          setBoot({ kind: 'setup' });
        } else {
          const lang = usePreferencesStore.getState().preferences.language;
          setBoot({ kind: 'ready', initialLanguage: lang });
        }

        // 6. Register the service worker (non-blocking; never throws).
        //    `onNeedRefresh` flips the update Toast on; the user's
        //    "Yenile" click invokes the captured `updateSW(true)` to
        //    skip-waiting and reload.
        void registerServiceWorker(
          () => {
            setUpdatePrompt((prev) => ({ ...prev, open: true }));
          },
          () => {
            // eslint-disable-next-line no-console
            console.info('[pwa] Offline ready.');
          },
        ).then((updateSW) => {
          if (updateSW === null || cancelled) return;
          setUpdatePrompt((prev) => ({
            open: prev.open,
            apply: () => {
              void updateSW(true);
            },
          }));
        });
      } catch (err) {
        if (cancelled) return;
        setBoot({ kind: 'error', message: 'Boot failed' });
        // eslint-disable-next-line no-console
        console.error('Boot error:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Global keyboard shortcuts (Requirement 10.2).
  // Settings live as an inline panel inside the chat surface, so
  // there's no global shortcut needed for them anymore — the gear
  // icon in the header is one tap away. Composer Enter-to-send and
  // per-component Esc handlers live in their own components.

  if (boot.kind === 'loading') {
    return <BootSplash message="Yükleniyor..." />;
  }

  if (boot.kind === 'error') {
    return <BootSplash message={boot.message} />;
  }

  // Local game preview is intentionally available before the companion
  // lock only when the explicit development flag is on. Production builds
  // keep the flag off, so neither the route nor this preview bypass exists
  // for visitors of the live site.
  const isGameRoute = typeof window !== 'undefined' && window.location.pathname === '/game';
  const isLocalGamePreview = import.meta.env.DEV && isGameFeatureEnabled() && isGameRoute;

  // Lock gate: render the LockRoute on top of everything until the
  // End_User has either set a fresh PIN or entered the existing one.
  // We render a minimal I18nProvider wrapper so the lock screen can
  // still use the live region and themed CSS variables without
  // having to mount the full router stack.
  if (!unlocked && !isLocalGamePreview) {
    /**
     * Post-unlock callback.
     *
     * Once the End_User has typed her passphrase, the user-key for
     * the sync server can be derived from the freshly-stored hash.
     * We pull the saved bundle (if any) and re-hydrate every slice
     * so the chat surface lands on the user's cross-device state
     * instead of whatever this device happened to have locally.
     *
     * Every step is best-effort: a failed pull or a missing server
     * URL just means the device falls back to its local state.
     */
    const handleUnlocked = async (): Promise<void> => {
      if (isSyncEnabled()) {
        try {
          // Suspend auto-sync while we pull + re-hydrate so the
          // re-hydration writes don't bounce back to the server.
          suspendAutoSync();
          const active = getActiveConversationStore();
          const result = await pullAndApply(active);
          if (result === 'applied') {
            // Re-hydrate every slice so the UI sees the imported
            // bundle instead of the pre-pull local state.
            await usePreferencesStore.getState().hydrate();
            await useProfileStore.getState().hydrate();
            await useCompanionNameStore.getState().hydrate();
            await useThemeStore.getState().hydrate();
            useNamingFlowStore
              .getState()
              .hydrate(useCompanionNameStore.getState().name);
            await useThreadsStore.getState().hydrate();
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[sync] pull on unlock failed:', err);
        } finally {
          // Re-enable mirroring; if no remote bundle existed, the
          // next local edit will push the first version.
          resumeAutoSync();
        }
      }
      setUnlocked(true);
    };

    return (
      <I18nProvider initialLanguage={boot.kind === 'ready' ? boot.initialLanguage : 'tr'}>
        <LockRoute onUnlocked={() => { void handleUnlocked(); }} />
      </I18nProvider>
    );
  }

  // Build the router only after boot resolves so routes can read from
  // fully-hydrated stores. The `/` route redirects to `/setup` when
  // no API key is configured (Requirement 6.7); the wildcard route
  // bounces unknown paths back to `/`. Settings now live as an
  // inline panel inside the chat surface — the legacy `/settings`
  // route still resolves so existing bookmarks work, but it just
  // hands off to the chat surface where the panel auto-opens.
  const isSetup = boot.kind === 'setup';
  const router = createBrowserRouter([
    {
      path: '/',
      element: isSetup ? <Navigate to="/setup" replace /> : <ChatRoute />,
    },
    {
      path: '/music',
      element: isSetup ? <Navigate to="/setup" replace /> : <MusicRoute />,
    },
    {
      path: '/game',
      element: !isSetup && isGameFeatureEnabled() ? (
        <Suspense fallback={<BootSplash message="Oyun yükleniyor..." />}>
          <GameRoute />
        </Suspense>
      ) : <Navigate to="/" replace />,
    },
    { path: '/watch', element: <Navigate to="/" replace /> },
    { path: '/settings', element: <Navigate to="/" replace /> },
    {
      path: '/setup',
      element: (
        <SetupRoute
          onSaved={() => {
            window.location.replace('/');
          }}
        />
      ),
    },
    { path: '*', element: <Navigate to="/" replace /> },
  ]);

  const initialLanguage: LanguageCode =
    boot.kind === 'ready' ? boot.initialLanguage : 'tr';

  return (
    <I18nProvider initialLanguage={initialLanguage}>
      {/* Skip-to-main link — first focusable element so keyboard users
          can bypass the sidebar quickly (Requirement 10.2). */}
      <a href="#main-content" className="skipLink">
        İçeriğe atla
      </a>

      {/* Global polite live region — components dispatch status
          updates here for screen readers (Requirement 10.3). */}
      <div
        id="aria-live-status"
        aria-live="polite"
        aria-atomic="true"
        className="srOnly"
      />

      {!isSetup && !isGameRoute ? <ThemeStage /> : null}
      <RouterProvider router={router} />

      <Toast
        open={persistenceWarning}
        tone="error"
        message="Yerel depolama kullanılamıyor. Mesajlar bu sekme kapanınca saklanmayacak."
        onClose={() => setPersistenceWarning(false)}
        durationMs={8000}
      />

      <Toast
        open={updatePrompt.open}
        tone="info"
        message="Yeni bir sürüm hazır. Yenilensin mi?"
        actionLabel="Yenile"
        onAction={updatePrompt.apply}
        onClose={() => setUpdatePrompt((prev) => ({ ...prev, open: false }))}
        durationMs={0}
      />
    </I18nProvider>
  );
}

/**
 * Pre-i18n splash. Used during boot loading and on a fatal boot
 * error. Renders without depending on the I18nProvider so it can show
 * even if i18n initialization itself failed.
 */
function BootSplash({ message }: { message: string }): ReactElement {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ff5d9e, #ffaad4)',
        color: 'white',
        fontFamily: 'system-ui, sans-serif',
      }}
      data-testid="boot-splash"
    >
      <p>{message}</p>
    </div>
  );
}
